import { FlowFolderListItem } from "src/clients/flow-api";
import {
  OrganizationUser,
  RoleInDB,
  RoleOperation,
  RoleOperationOpEnum,
  SimpleRoleType,
} from "src/clients/taktile-api";
import {
  DUMMY_KEY_COUNTS,
  FOLDER_DEPENDENCY_KEY,
  FOLDER_GRANULAR_ROLES,
  GranularRole,
  GranularRoleKey,
  LOGIC_ALL_FOLDERS_VERTICAL,
  NO_FOLDER_ARGUMENT,
  NO_WORKSPACE_ROLE,
  VerticalDependencyKey,
  WILDCARD_ARGUMENT,
  WORKSPACE_ROLES,
  WORKSPACE_VERTICALS,
} from "src/userManagement/userPermissions/permissionFeatures";

const FOLDER_ROW_PREFIX = "folder-";

export type GranularRolesRow = {
  id: string;
  label: string;
  isFolder: boolean;
  granularRoles: GranularRole[];
  dependencyKey: VerticalDependencyKey;
  keysCount: number;
};

export type GranularRolesFormValues = Record<string, GranularRoleKey[]>;

export type UserPermissionsFormValues = {
  orgRole: SimpleRoleType;
  workspaceRole: string;
  granularRoles: GranularRolesFormValues;
};

type GranularRolesSnapshot = Record<string, GranularRoleKey[] | undefined>;

type UserPermissionsSnapshot = {
  orgRole?: SimpleRoleType;
  workspaceRole?: string;
  granularRoles?: GranularRolesSnapshot;
};

export type GranularRolesFieldName = `granularRoles.${string}`;

export const granularRolesFieldName = (rowId: string): GranularRolesFieldName =>
  `granularRoles.${rowId}`;

const getFolderRowId = (folderId: string): string =>
  `${FOLDER_ROW_PREFIX}${folderId}`;

export const getGranularRolesRows = (
  folders: FlowFolderListItem[],
): GranularRolesRow[] => [
  ...[...WORKSPACE_VERTICALS, LOGIC_ALL_FOLDERS_VERTICAL].map(
    (vertical): GranularRolesRow => ({
      id: vertical.key,
      label: vertical.label,
      isFolder: false,
      granularRoles: vertical.granularRoles,
      dependencyKey: vertical.key,
      keysCount: DUMMY_KEY_COUNTS[vertical.key] ?? 0,
    }),
  ),
  ...folders.map(
    (folder): GranularRolesRow => ({
      id: getFolderRowId(folder.id),
      label: folder.name,
      isFolder: true,
      granularRoles: FOLDER_GRANULAR_ROLES,
      dependencyKey: FOLDER_DEPENDENCY_KEY,
      keysCount: 0,
    }),
  ),
  {
    id: getFolderRowId(NO_FOLDER_ARGUMENT),
    label: "No folder",
    isFolder: true,
    granularRoles: FOLDER_GRANULAR_ROLES,
    dependencyKey: FOLDER_DEPENDENCY_KEY,
    keysCount: 0,
  },
];

const findRowIdByRoleName = (
  roleName: string,
  roleArgs: RoleInDB["arguments"],
): string | null => {
  if (roleName.startsWith("agent_")) {
    return "agents";
  }
  if (roleName.startsWith("connection_")) {
    return "connections";
  }
  if (roleName.startsWith("dh_")) {
    return "decision_history";
  }
  if (
    roleName.startsWith("data_") ||
    roleName.startsWith("file_user") ||
    roleName.startsWith("case_")
  ) {
    return "data_and_cases";
  }

  if (roleName.startsWith("flow_")) {
    if (roleArgs.flow_folder_id === WILDCARD_ARGUMENT) {
      return LOGIC_ALL_FOLDERS_VERTICAL.key;
    }

    return roleArgs.flow_folder_id
      ? getFolderRowId(roleArgs.flow_folder_id)
      : null;
  }

  return null;
};

export const getInitialGranularRoles = (
  roles: RoleInDB[],
  folders: FlowFolderListItem[],
): GranularRolesFormValues => {
  const granularRolesByRow = roles.reduce<GranularRolesFormValues>(
    (acc, role) => {
      const rowId = findRowIdByRoleName(role.name, role.arguments);
      if (rowId) {
        acc[rowId] ||= [];
        acc[rowId].push(role.name as GranularRoleKey);
      }

      return acc;
    },
    {},
  );

  return Object.fromEntries(
    getGranularRolesRows(folders).map((row) => [
      row.id,
      granularRolesByRow[row.id] ?? [],
    ]),
  );
};

export const getInitialFormValues = ({
  user,
  roles,
  folders,
}: {
  user: OrganizationUser;
  roles: RoleInDB[];
  folders: FlowFolderListItem[];
}): UserPermissionsFormValues => ({
  orgRole: user.role ?? SimpleRoleType.MEMBER,
  workspaceRole:
    roles.find((role) => WORKSPACE_ROLES.includes(role.name))?.name ??
    NO_WORKSPACE_ROLE,
  granularRoles: getInitialGranularRoles(roles, folders),
});

const getRoleArguments = (
  rowId: string,
  { orgId, workspaceId }: { orgId: string; workspaceId: string },
): RoleOperation["arguments"] => {
  const workspaceArguments = { org_id: orgId, ws_id: workspaceId };

  if (rowId === LOGIC_ALL_FOLDERS_VERTICAL.key) {
    return { ...workspaceArguments, flow_folder_id: WILDCARD_ARGUMENT };
  }

  if (rowId.startsWith(FOLDER_ROW_PREFIX)) {
    return {
      ...workspaceArguments,
      flow_folder_id: rowId.slice(FOLDER_ROW_PREFIX.length),
    };
  }

  return workspaceArguments;
};

const getWorkspaceRoleOperations = ({
  baseline,
  current,
  roleArguments,
}: {
  baseline?: string;
  current?: string;
  roleArguments: RoleOperation["arguments"];
}): RoleOperation[] => {
  const revoked = baseline ?? NO_WORKSPACE_ROLE;
  const granted = current ?? NO_WORKSPACE_ROLE;

  if (revoked === granted) {
    return [];
  }

  return [
    ...(revoked === NO_WORKSPACE_ROLE
      ? []
      : [
          {
            op: RoleOperationOpEnum.REVOKE,
            name: revoked,
            arguments: roleArguments,
          },
        ]),
    ...(granted === NO_WORKSPACE_ROLE
      ? []
      : [
          {
            op: RoleOperationOpEnum.GRANT,
            name: granted,
            arguments: roleArguments,
          },
        ]),
  ];
};

const getGranularRoleOperations = ({
  baseline,
  current,
  orgId,
  workspaceId,
}: {
  baseline: GranularRolesSnapshot;
  current: GranularRolesSnapshot;
  orgId: string;
  workspaceId: string;
}): RoleOperation[] =>
  [...new Set([...Object.keys(baseline), ...Object.keys(current)])].flatMap(
    (rowId) => {
      const granted = current[rowId] ?? [];
      const revoked = baseline[rowId] ?? [];
      const roleArguments = getRoleArguments(rowId, { orgId, workspaceId });

      return [
        ...granted
          .filter((name) => !revoked.includes(name))
          .map((name) => ({
            op: RoleOperationOpEnum.GRANT,
            name,
            arguments: roleArguments,
          })),
        ...revoked
          .filter((name) => !granted.includes(name))
          .map((name) => ({
            op: RoleOperationOpEnum.REVOKE,
            name,
            arguments: roleArguments,
          })),
      ];
    },
  );

export const getRoleOperations = ({
  baseline,
  current,
  orgId,
  workspaceId,
}: {
  baseline: UserPermissionsSnapshot;
  current: UserPermissionsSnapshot;
  orgId: string;
  workspaceId: string;
}): RoleOperation[] => [
  ...getWorkspaceRoleOperations({
    baseline: baseline.workspaceRole,
    current: current.workspaceRole,
    roleArguments: { org_id: orgId, ws_id: workspaceId },
  }),
  ...getGranularRoleOperations({
    baseline: baseline.granularRoles ?? {},
    current: current.granularRoles ?? {},
    orgId,
    workspaceId,
  }),
];
